import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tag, Plus, Trash2, Save, X, Infinity as InfinityIcon, Percent, CalendarClock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

const BLANK = {
  code: "",
  target_plan_id: "",
  trial_days: 0,
  months_until_upgrade: 0,
  upgrade_plan_id: "",
  is_lifetime_free: false,
  is_10_percent_once_off: false,
  is_expired: false,
};

const PLAN_OPTIONS = [
  { value: "", label: "Any plan (universal)" },
  { value: "parent_launch", label: "Parent — launch" },
  { value: "parent_normal", label: "Parent — standard" },
  { value: "vendor_launch", label: "Vendor — launch" },
  { value: "vendor_normal", label: "Vendor — standard" },
  { value: "user_bundle_launch", label: "Bundle — launch" },
  { value: "user_bundle_normal", label: "Bundle — standard" },
];

// Admin UI for voucher codes. Lets admins add new codes, patch any field
// inline, flip the expired flag (immediately disabling the code at the
// public /promo-codes/validate endpoint), and hard-delete.
export default function PromoCodesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(BLANK);
  const [creating, setCreating] = useState(false);
  const [edits, setEdits] = useState({}); // keyed by code

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load vouchers"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const code = (draft.code || "").trim().toUpperCase();
    if (!code) { toast.error("Code is required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/promo-codes", { ...draft, code });
      toast.success(`Voucher ${code} created`);
      setDraft(BLANK);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not create voucher"));
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async (code) => {
    const patch = edits[code];
    if (!patch) return;
    try {
      await api.patch(`/admin/promo-codes/${encodeURIComponent(code)}`, patch);
      toast.success(`Voucher ${code} updated`);
      setEdits((e) => { const n = { ...e }; delete n[code]; return n; });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update voucher"));
    }
  };

  const toggleExpired = async (code, next) => {
    try {
      await api.patch(`/admin/promo-codes/${encodeURIComponent(code)}`, { is_expired: next });
      toast.success(next ? `Voucher ${code} expired` : `Voucher ${code} reactivated`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update voucher"));
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Permanently delete voucher ${code}? Expiring instead keeps the audit trail.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${encodeURIComponent(code)}`);
      toast.success(`Voucher ${code} deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete voucher"));
    }
  };

  const editFieldFor = (p, key) => edits[p.code]?.[key] ?? p[key];
  const setEditField = (p, key, value) =>
    setEdits((e) => ({ ...e, [p.code]: { ...(e[p.code] || {}), [key]: value } }));
  const isDirty = (p) => edits[p.code] && Object.keys(edits[p.code]).length > 0;

  return (
    <div className="space-y-6" data-testid="promo-codes-tab">
      {/* ─── New voucher form ─── */}
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm">
        <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-sage-dark" /> New voucher
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            placeholder="CODE"
            className="rounded-xl bg-softstone/60 border-sage/30 font-mono tracking-wider"
            data-testid="new-promo-code-input"
          />
          <select
            value={draft.target_plan_id}
            onChange={(e) => setDraft({ ...draft, target_plan_id: e.target.value })}
            className="rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-sm text-forest"
            data-testid="new-promo-plan-select"
          >
            {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={draft.upgrade_plan_id}
            onChange={(e) => setDraft({ ...draft, upgrade_plan_id: e.target.value })}
            className="rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-sm text-forest"
            data-testid="new-promo-upgrade-plan-select"
          >
            <option value="">Upgrade plan — none</option>
            {PLAN_OPTIONS.filter((o) => o.value).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-forest/70">
            <span className="whitespace-nowrap">Trial days</span>
            <Input
              type="number" min={0}
              value={draft.trial_days}
              onChange={(e) => setDraft({ ...draft, trial_days: Number(e.target.value) || 0 })}
              className="rounded-xl bg-softstone/60 border-sage/30"
              data-testid="new-promo-trial-days"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-forest/70">
            <span className="whitespace-nowrap">Months to upgrade</span>
            <Input
              type="number" min={0}
              value={draft.months_until_upgrade}
              onChange={(e) => setDraft({ ...draft, months_until_upgrade: Number(e.target.value) || 0 })}
              className="rounded-xl bg-softstone/60 border-sage/30"
              data-testid="new-promo-months-upgrade"
            />
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-forest/70">
              <Switch
                checked={draft.is_lifetime_free}
                onCheckedChange={(v) => setDraft({ ...draft, is_lifetime_free: v })}
                data-testid="new-promo-lifetime-switch"
              />
              <InfinityIcon className="w-4 h-4" /> Lifetime free
            </label>
            <label className="flex items-center gap-2 text-sm text-forest/70">
              <Switch
                checked={draft.is_10_percent_once_off}
                onCheckedChange={(v) => setDraft({ ...draft, is_10_percent_once_off: v })}
                data-testid="new-promo-cashback-switch"
              />
              <Percent className="w-4 h-4" /> 10% cashback
            </label>
          </div>
        </div>
        <div className="mt-4 text-right">
          <Button
            onClick={create}
            disabled={creating || !draft.code.trim()}
            className="rounded-full bg-sage hover:bg-sage-dark text-white"
            data-testid="new-promo-submit-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Create voucher
          </Button>
        </div>
      </div>

      {/* ─── Existing vouchers ─── */}
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm">
        <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2">
          <Tag className="w-5 h-5 text-terracotta-dark" /> Existing vouchers
        </h3>
        {loading ? (
          <p className="text-sm text-forest/60">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-forest/60">No vouchers yet.</p>
        ) : (
          <div className="space-y-3">
            {items.map((p) => (
              <div
                key={p.code}
                className={`rounded-2xl border p-4 ${p.is_expired ? "border-forest/15 bg-softstone/40 opacity-70" : "border-sage/20 bg-white/60"}`}
                data-testid={`promo-row-${p.code}`}
              >
                <div className="flex items-center flex-wrap gap-3 mb-3">
                  <span className="font-mono font-black text-forest tracking-wider text-base" data-testid={`promo-code-${p.code}`}>{p.code}</span>
                  {p.is_lifetime_free && <span className="text-[10px] font-bold uppercase bg-sage-light text-sage-dark px-2 py-0.5 rounded-full">Lifetime</span>}
                  {p.is_10_percent_once_off && <span className="text-[10px] font-bold uppercase bg-terracotta-light text-terracotta-dark px-2 py-0.5 rounded-full">10% cashback</span>}
                  {p.is_expired && <span className="text-[10px] font-bold uppercase bg-forest/10 text-forest/60 px-2 py-0.5 rounded-full flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Expired</span>}
                  <label className="ml-auto flex items-center gap-2 text-xs text-forest/70">
                    <Switch
                      checked={!p.is_expired}
                      onCheckedChange={(v) => toggleExpired(p.code, !v)}
                      data-testid={`promo-active-switch-${p.code}`}
                    />
                    {p.is_expired ? "Reactivate" : "Active"}
                  </label>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <label className="text-xs text-forest/60 flex flex-col gap-1">
                    Target plan
                    <select
                      value={editFieldFor(p, "target_plan_id") || ""}
                      onChange={(e) => setEditField(p, "target_plan_id", e.target.value)}
                      className="rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-sm text-forest"
                      data-testid={`promo-target-${p.code}`}
                    >
                      {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-forest/60 flex flex-col gap-1">
                    Upgrade plan
                    <select
                      value={editFieldFor(p, "upgrade_plan_id") || ""}
                      onChange={(e) => setEditField(p, "upgrade_plan_id", e.target.value)}
                      className="rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-sm text-forest"
                      data-testid={`promo-upgrade-${p.code}`}
                    >
                      <option value="">None</option>
                      {PLAN_OPTIONS.filter((o) => o.value).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs text-forest/60 flex flex-col gap-1">
                    Trial days
                    <Input
                      type="number" min={0}
                      value={editFieldFor(p, "trial_days") ?? 0}
                      onChange={(e) => setEditField(p, "trial_days", Number(e.target.value) || 0)}
                      className="rounded-xl bg-softstone/60 border-sage/30"
                      data-testid={`promo-trial-${p.code}`}
                    />
                  </label>
                  <label className="text-xs text-forest/60 flex flex-col gap-1">
                    Months to upgrade
                    <Input
                      type="number" min={0}
                      value={editFieldFor(p, "months_until_upgrade") ?? 0}
                      onChange={(e) => setEditField(p, "months_until_upgrade", Number(e.target.value) || 0)}
                      className="rounded-xl bg-softstone/60 border-sage/30"
                      data-testid={`promo-months-${p.code}`}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-forest/70 pt-5">
                    <Switch
                      checked={!!editFieldFor(p, "is_lifetime_free")}
                      onCheckedChange={(v) => setEditField(p, "is_lifetime_free", v)}
                      data-testid={`promo-lifetime-${p.code}`}
                    />
                    <InfinityIcon className="w-3.5 h-3.5" /> Lifetime free
                  </label>
                  <label className="flex items-center gap-2 text-xs text-forest/70 pt-5">
                    <Switch
                      checked={!!editFieldFor(p, "is_10_percent_once_off")}
                      onCheckedChange={(v) => setEditField(p, "is_10_percent_once_off", v)}
                      data-testid={`promo-cashback-${p.code}`}
                    />
                    <Percent className="w-3.5 h-3.5" /> 10% cashback
                  </label>
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                  {isDirty(p) && (
                    <>
                      <Button
                        variant="ghost"
                        className="rounded-full text-forest/60"
                        onClick={() => setEdits((e) => { const n = { ...e }; delete n[p.code]; return n; })}
                        data-testid={`promo-cancel-${p.code}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button
                        className="rounded-full bg-sage hover:bg-sage-dark text-white"
                        onClick={() => saveEdit(p.code)}
                        data-testid={`promo-save-${p.code}`}
                      >
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    className="rounded-full text-terracotta-dark hover:bg-terracotta-light"
                    onClick={() => remove(p.code)}
                    data-testid={`promo-delete-${p.code}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
